// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

type Props = {
    category?: Category;
    withHoverShadow?: boolean;
    extendedStyles?: CategoryItemStyles;
    widgetView?: string;
    path?: string;
    smallImagePath?: string;
    name?: string;
};

interface CategoryItemStyles {
    wrapper?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryName?: TypographyProps;
    imageWrapper?: InjectableCss;
}

const wrapperWithShadow = `
        box-shadow: 0 0 25px 0 rgba(79, 79, 119, 0.18);
        border-radius: 4px;
        background-color: ${getColor("common.background")};
`;

export const categoryItemStyles: CategoryItemStyles = {
    wrapper: {
        css: css`
            background: transparent;
            height: 100%;
            display: block;
            &:hover {
                ${(props: any) => (props.withHoverShadow ? wrapperWithShadow : "")}
                text-decoration: none;
                > span {
                    color: ${getColor("primary")};
                }
            }
            > span {
                display: flex;
                flex-direction: column;
                justify-content: center;
                height: 100%;
                width: 100%;
            }
        `,
    },
    imageWrapper: {
        css: css`
            height: 120px;
            width: 100%;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            overflow: hidden;
        `,
    },
    categoryImage: {
        width: "100%",
        height: "120px",
    },
    categoryName: {
        weight: fontConfig.weight.semiBold,
        lineHeight: fontConfig.lineHeight.singleLineSmall,
        css: css`
            margin-top: 9px;
            padding: 0px 12px 20px 12px;
            text-align: center;
            white-space: pre;
            opacity: 0.8;
        `,
    },
};

const CategoryItem = ({ category, withHoverShadow, extendedStyles, widgetView, path, smallImagePath, name }: Props) => {
    const [styles] = React.useState(() => mergeToNew(categoryItemStyles, extendedStyles));
    const imageSrc = category?.smallImagePath || smallImagePath;

    return (
        <Link
            href={category?.path || path}
            {...{ withHoverShadow }}
            aria-label={category?.imageAltText || ""}
            css={css`
                ${styles.wrapper?.css};
                width: 100%;
                ${widgetView === "carousel" &&
                `            max-width: 193px;
                `};
            `}
        >
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage
                    altText={category?.imageAltText || ""}
                    src={imageSrc}
                    width="120px"
                    height="120px"
                    cloudinaryWidth="120"
                    cloudinary={true}
                />
            </StyledWrapper>
            <Typography {...styles.categoryName} ellipsis as="h3">
                {category?.name || category?.shortDescription || name}
            </Typography>
        </Link>
    );
};

export default CategoryItem;
